import { IconButton } from '@fox/atoms/src/icon-button/index';
import { Popover } from '@fox/atoms/src/popover/index';
import { Select } from '@fox/atoms/src/select/index';
import { StaticDateSelector } from '@fox/atoms/src/static-date-selector/index';
import { Typography } from '@fox/atoms/src/typography/index';
import moment, { Moment } from 'moment';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { FilterExtensionProps } from '../contracts/filters';

function validateDateString(dateString: string) {
	return moment(dateString, ['DD-MM-YYYY'], true).isValid();
}

const options: string[] = ['@today', '@today(+1d)', '@today(-1d)', '@startOfDay', '@startOfWeek', '@startOfMonth'];

type DateFilterProps = FilterExtensionProps<string> & { placeholder?: string; width?: number | string };

export function DateFilter(props: DateFilterProps) {
	return <DateOrDateTimeFilter {...props} formatServer={a => a.format('YYYY-MM-DD')} />;
}

export function DateTimeFilter(props: DateFilterProps) {
	return <DateOrDateTimeFilter {...props} formatServer={a => a.utc().toISOString()} />;
}

function DateOrDateTimeFilter({
	value,
	setValue,
	placeholder,
	readOnly,
	width,
	formatServer
}: DateFilterProps & { formatServer: (moment: Moment) => string }) {
	const [vis, setVis] = useState(false);
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [error, setError] = useState(false);
	const theme = useTheme();

	const handleVis = () => {
		setVis(!vis);
		setOpen(false);
	};

	return (
		<div>
			<Select<string, false, true, true>
				disableClearable={true}
				disabled={readOnly}
				size={'small'}
				style={{ width: width ?? '100%' }}
				onChange={(e, v) => {
					if (e?.type === 'click') {
						if (v.startsWith('@')) {
							setValue(v);
							setError(false);
							setSelected(false);
						} else if (!validateDateString(v)) {
							setError(true);
						} else if (v === '') {
							setError(true);
						} else {
							setInputValue(v);
							setError(false);
						}
					}
				}}
				placeholder={placeholder}
				onInputChange={(_e, v, _r) => {
					if (readOnly) return;
					setInputValue(v);
				}}
				value={value ?? ''}
				options={options}
				open={open}
				freeSolo={true}
				onBlur={() => {
					setValue(inputValue);
					if (moment(value).isValid()) {
						setValue(formatServer(moment(inputValue, 'DD/MM/YYYY')));
					}
				}}
				getOptionLabel={option => {
					if (option.startsWith('@')) {
						return option;
					} else if (option === '') {
						return option;
					} else if (typeof option === 'object') {
						return moment(option).format('DD/MM/YYYY');
					} else {
						return moment(option).format('DD/MM/YYYY');
					}
				}}
				autoComplete={true}
				onOpen={() => {
					if (readOnly) return;
					if (!vis) setOpen(true);
				}}
				onClose={() => {
					setOpen(false);
				}}
				endAdornment={
					readOnly ? undefined : (
						<>
							<Popover
								overlay={
									<StaticDateSelector
										value={value ? moment.utc(value).toDate() : null}
										onChange={c => {
											const defaultedDate = moment(c).hours(0).minutes(0).seconds(0).milliseconds(0);
											setValue(formatServer(moment(defaultedDate)));
											setInputValue(moment(defaultedDate).utc().toISOString());
											setSelected(true);
											setError(false);
										}}
										displayStaticWrapperAs="desktop"
									/>
								}
								visible={vis}
								onVisibleChange={setVis}
								trigger="click"
							>
								<IconButton
									// This icon is forcing size growth on the Select due to the icon button's default padding
									style={{ backgroundColor: selected ? theme.palette.grey[200] : '', padding: 0 }}
									iconName="CalendarMonth"
									size={'small'}
									onClick={handleVis}
									disableFocusRipple
									disableRipple
								/>
							</Popover>
						</>
					)
				}
			/>
			<div style={{ position: 'absolute' }}>
				{error ? (
					<Typography variant="caption" color={theme.colors.bad}>
						Please enter a valid date eg 21/03/2023
					</Typography>
				) : (
					<></>
				)}
			</div>
		</div>
	);
}
